<template>
  <div class="rs-toggle-wrapper" :style="wrapperStyle">
    <ul class="rs-toggle" :class="{ disabled }" :style="layoutStyle">
      <li :style="itemStyle" v-for="(item, index) in internalItems" :key="index">
        <input
          :disabled="isDisabled(item)"
          :id="id(item)"
          :name="name"
          type="radio"
          v-on:click="toggle($event, item)"
        />
        <label
          :style="labelStyle(item)"
          :class="{ selected: isSelected(item), disabled: isDisabled(item) }"
          :for="id(item)"
          type="radio"
        >
          {{ item[itemText] }}
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
import deepMerge from 'deepmerge'
import deepEqual from 'fast-deep-equal'

const rem = (v) => v + 'rem'
const px = (v) => v + 'px'

const defaultOptions = () => ({
  layout: {
    color: 'black',
    backgroundColor: 'lightgray',
    selectedColor: 'white',
    selectedBackgroundColor: 'var(--rs-accent-color)',
    borderColor: '#b5b5b5',
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'normal',
    fontWeightSelected: 'normal',
    noBorder: false
  },
  size: {
    fontSize: 14,
    height: 28,
    padding: 0,
    width: '1fr'
  }
})

export default {
  name: 'RsToggle',
  props: {
    itemValue: {
      type: [Object, String, Number, Boolean],
      default: 'value'
    },
    itemText: {
      type: String,
      default: 'label'
    },
    items: {
      type: Array,
      default: () => []
    },
    options: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: [Object, String, Number, Boolean],
      required: false
    },
    name: {
      type: String,
      required: false
    },
    group: {
      type: String,
      required: false,
      default: ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    disabledItemValue: {
      type: String,
      required: false,
      default: 'disabled'
    },
    itemWidth: {
      type: String,
      default: 'auto'
    },
    fullWidth: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  emits: ['update:modelValue', 'change'],

  data() {
    return {
      internalValue: null,
      internalOptions: defaultOptions()
    }
  },

  created() {
    this.internalValue = this.modelValue
    this.mergeDefaultOptionsWithProp(this.options)
  },

  computed: {
    internalItems() {
      return this.items.map((item) => item)
    },
    isSelected() {
      return (item) => {
        if (!this.itemValue) return deepEqual(item, this.internalValue)
        if (this.itemValue && Object.prototype.hasOwnProperty.call(item, this.itemValue)) {
          return this.internalValue === item[this.itemValue]
        }
        return false
      }
    },
    isDisabled() {
      return (item) => this.disabled || item[this.disabledItemValue]
    },
    id() {
      return (item) => (item[this.itemText] + this.group).toLowerCase()
    },
    wrapperStyle() {
      return { }
    },
    layoutStyle() {
      if (this.itemWidth !== 'auto') {
        return {
          gridTemplateColumns: `repeat(auto-fill, minmax(${this.itemWidth}, 1fr))`
        }
      }

      return {
        gridTemplateColumns: `repeat(${this.items.length}, ${this.internalOptions.size.width})`
      }
    },
    itemStyle() {
      return {
        height: px(this.internalOptions.size.height),
        fontFamily: this.internalOptions.layout.fontFamily,
        fontSize: px(this.internalOptions.size.fontSize),
        textAlign: 'center'
      }
    },
    labelStyle() {
      return (item) => {
        if (this.isSelected(item)) return this.labelSelectedStyle(item.color, item.backgroundColor);

        return {
          padding: rem(this.internalOptions.size.padding),
          borderColor: this.internalOptions.layout.noBorder
            ? 'transparent'
            : this.internalOptions.layout.borderColor,
          backgroundColor: this.internalOptions.layout.backgroundColor,
          color: this.internalOptions.layout.color,
          fontWeight: this.internalOptions.layout.fontWeight
        }
      }
    },
    labelSelectedStyle() {
      return (color, backgroundColor) => ({
        padding: rem(this.internalOptions.size.padding),
        borderColor: this.internalOptions.layout.noBorder
          ? 'transparent'
          : this.internalOptions.layout.borderColor,
        fontWeight: this.internalOptions.layout.fontWeightSelected,
        backgroundColor: backgroundColor ?? this.internalOptions.layout.selectedBackgroundColor,
        color: color ?? this.internalOptions.layout.selectedColor
      })
    }
  },

  methods: {
    toggle(event, value) {
      if (this.disabled) return

      let result = value
      if (this.itemValue && Object.prototype.hasOwnProperty.call(value, this.itemValue))
        result = result[this.itemValue]
      this.$emit('update:modelValue', result)
      this.$emit('change', { value: result, event })
    },
    mergeDefaultOptionsWithProp(options) {
      this.internalOptions = deepMerge(this.internalOptions, options)
    }
  },

  watch: {
    modelValue(value) {
      this.internalValue = value
    },
    options(value) {
      value && this.mergeDefaultOptionsWithProp(value)
    }
  }
}
</script>

<style lang="scss" scoped>
ul {
  list-style: none;
}

label {
  margin: 0;
}

.rs-toggle-wrapper {
  width: 100%;
  background-color: #fff;
  border: 1px solid #b5b5b5;
  border-radius: 3px;
  padding: 5px;
}

.rs-toggle {
  display: grid;
  gap: 5px;
  padding: 0;
  margin: 0;

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.rs-toggle li {
  position: relative;
  margin-bottom: 0;

  label {
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: 0.3s background-color ease;
    border-radius: 3px;
    border: 1px solid;

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}

.rs-toggle label,
.rs-toggle input {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.rs-toggle input[type='radio'] {
  opacity: 0.01;
  z-index: 1;
}

.rs-toggle li:not(:first-child):not(:last-child) label {
  border-right: 1px solid;
  border-top: 1px solid;
  border-bottom: 1px solid;
}
</style>
